import React, { useEffect, useMemo, useState } from "react";
import { Empty, Spin, Tag } from "antd";
import { useNavigate } from "react-router-dom";
import { getCoordinatorHome } from "../../../../services/features/dashboard.service";
import StalledPill from "../../../components/StalledPill/StalledPill";
import {
  getSiteVisitStatus,
  resolveSiteVisitStatusName,
} from "../../../../services/Enums/siteVisitStatusEnum";
import { formatDate } from "../../../../services/helpFunctions/formatDateAndTime";
import { HOME_COLORS } from "../homeTheme";

// F-01 — Field Coordinator home. One call
// (/api/Dashboard/coordinatorHome) returns the coordinator's own visit queue
// plus the three counts, so this is the landing view: what's assigned, what's
// still pending, and what has gone quiet.
//
// Status arrives as either the numeric SiteVisitStatus or its enum name
// depending on the serialiser, so resolve both.
const statusOf = (status) =>
  typeof status === "number" || /^\d+$/.test(String(status ?? ""))
    ? getSiteVisitStatus(Number(status))
    : resolveSiteVisitStatusName(status);

// Anything not yet completed is still pending work for the coordinator.
const COMPLETED_STATUS = 6;
const isPending = (v) => {
  const s = statusOf(v?.status);
  return s.text !== getSiteVisitStatus(COMPLETED_STATUS).text;
};

const CoordinatorHomePanel = () => {
  const navigate = useNavigate();
  const projectID = localStorage.getItem("currentProjectID");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!projectID) {
      setLoading(false);
      return;
    }
    getCoordinatorHome(
      projectID,
      (res) => {
        setData(res?.data || res || null);
        setLoading(false);
      },
      () => {
        setFailed(true);
        setLoading(false);
      }
    );
  }, [projectID]);

  // Pending first, then stalled ahead of the rest inside each group, then by
  // planned date so the nearest work is at the top.
  const visits = useMemo(() => {
    const list = Array.isArray(data?.visits) ? [...data.visits] : [];
    const rank = (v) => (isPending(v) ? 0 : 1);
    list.sort((a, b) => {
      if (rank(a) !== rank(b)) return rank(a) - rank(b);
      if (!!b.isStalled !== !!a.isStalled) return b.isStalled ? 1 : -1;
      const da = a.plannedVisit ? new Date(a.plannedVisit).getTime() : Infinity;
      const db = b.plannedVisit ? new Date(b.plannedVisit).getTime() : Infinity;
      return da - db;
    });
    return list;
  }, [data]);

  if (loading) {
    return (
      <div className="homePanel">
        <div className="homePanelLoading">
          <Spin />
        </div>
      </div>
    );
  }

  if (failed || !data) {
    return (
      <div className="homePanel">
        <div className="homePanelHead">
          <h3>Your visits</h3>
        </div>
        <Empty description="Couldn't load your visit queue" />
      </div>
    );
  }

  const KPIS = [
    {
      key: "total",
      value: data.totalVisits || 0,
      label: "Assigned visits",
      color: HOME_COLORS.brand,
    },
    {
      key: "pending",
      value: data.pendingVisits || 0,
      label: "Pending",
      color: HOME_COLORS.warn,
    },
    {
      key: "stalled",
      value: data.stalledVisits || 0,
      label: "Stalled",
      color: HOME_COLORS.stalled,
    },
  ];

  return (
    <div className="homePanel">
      <div className="homePanelHead">
        <h3>Your visits</h3>
        <span
          className="homePanelLink"
          onClick={() => navigate("/project/planning?visits")}
        >
          Visits Board
        </span>
      </div>

      <div className="homeHubKpis">
        {KPIS.map((k) => (
          <div
            key={k.key}
            className="homeHubKpi"
            onClick={() => navigate("/project/planning?visits")}
            role="button"
          >
            <span className="homeHubKpiValue">{k.value}</span>
            <span className="homeHubKpiLabel">
              <span className="homeDot" style={{ background: k.color }} />
              {k.label}
            </span>
          </div>
        ))}
      </div>

      {visits.length === 0 ? (
        <Empty description="No visits assigned to you on this project" />
      ) : (
        <div className="homeListRows" style={{ marginTop: 16 }}>
          {visits.map((v) => {
            const status = statusOf(v.status);
            return (
              <div
                className={`homeListRow ${
                  v.isStalled ? "homeListRow--stalled" : ""
                }`}
                key={v.siteVisitId}
                onClick={() => navigate("/project/planning?visits")}
              >
                <div className="homeListRowMain">
                  <span className="homeListRowTitle">
                    {v.name || v.code || "Site visit"}
                  </span>
                  <span className="homeListRowSub">
                    {v.code ? `${v.code} · ` : ""}
                    {v.updatedDate
                      ? `updated ${formatDate(v.updatedDate)}`
                      : "never updated"}
                  </span>
                </div>
                <div className="homeListRowSide">
                  {v.isStalled && <StalledPill />}
                  {v.plannedVisit && <Tag>{formatDate(v.plannedVisit)}</Tag>}
                  <span className="homeStatusDot">
                    <svg width="8" height="8" viewBox="0 0 9 9">
                      <circle cx="4.5" cy="4.27" r="4" fill={status.fill} />
                    </svg>
                    {status.text}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CoordinatorHomePanel;
